import FormattedMessage from 'I18n/components/FormattedMessage';
import { BaseSaveViewActionButton } from './BaseSaveViewActionButton';
import { useCallback, useState } from 'react';
import { useCanUserWriteViews } from '../../utils/hooks/useCanUserWriteViews';
import { SaveEditableViewIcon } from 'foundations-assets';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
const BoldCopy = ({
  children
}) => {
  return /*#__PURE__*/_jsx(Text, {
    as: "p",
    typography: "body-125",
    children: children
  });
};
const getSaveTooltip = ({
  isModifiedView,
  isReadOnlyView,
  canUserWriteViews
}) => {
  const tooltipUse = 'longform';
  if (isReadOnlyView) {
    return {
      headingText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.saveViewButtons.tooltips.save.readOnly.header"
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.saveViewButtons.tooltips.save.readOnly.body"
      }),
      use: tooltipUse
    };
  }
  if (!isModifiedView) {
    return {
      headingText: undefined,
      title: /*#__PURE__*/_jsx(BoldCopy, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "viewsManagement.saveViewButtons.tooltips.save.disabled"
        })
      }),
      use: tooltipUse
    };
  }
  if (!canUserWriteViews) {
    return {
      headingText: undefined,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.saveViewButtons.tooltips.save.notPermissioned"
      })
    };
  }
  return {
    headingText: undefined,
    title: /*#__PURE__*/_jsx(BoldCopy, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.saveViewButtons.tooltips.save.active"
      })
    }),
    use: tooltipUse
  };
};

// when a user saves a change to a view, they will click this button, see an alert popup, and then immediately see the tooltip change to
// "there are no edits to save"
// this UX is kinda confusing, it makes the user second guess if they actually saved anything
// to combat this, we will add a 1.5s delay in between when they click and when the tooltip changes to "there are no changes to save"
const useSaveButtonTooltipDebounce = ({
  onClick
}) => {
  const [shouldHideTooltip, setShouldHideTooltip] = useState(false);
  const wrappedOnClick = useCallback(() => {
    if (onClick) {
      onClick();
    }
    setShouldHideTooltip(true);
    setTimeout(() => {
      setShouldHideTooltip(false);
    }, 1500);
  }, [onClick]);
  return {
    shouldHideTooltip,
    wrappedOnClick
  };
};

//we should disable the save button for any of the following conditions
// - the user is trying to save a read only view (aka one they dont own, or is a hubspot owned view)
// - the user is trying to save a view when there are no changes to save
const getDisabled = ({
  isReadOnlyView,
  isModifiedView,
  canUserWriteViews
}) => {
  return isReadOnlyView || !isModifiedView || !canUserWriteViews;
};
export const SaveButton = ({
  isModifiedView,
  isReadOnlyView,
  onClick
}) => {
  const {
    shouldHideTooltip,
    wrappedOnClick
  } = useSaveButtonTooltipDebounce({
    onClick
  });
  const canUserWriteViews = useCanUserWriteViews();
  const tooltipProps = shouldHideTooltip ? undefined : getSaveTooltip({
    isModifiedView,
    isReadOnlyView,
    canUserWriteViews
  });
  const disabled = getDisabled({
    isModifiedView,
    isReadOnlyView,
    canUserWriteViews
  });
  return /*#__PURE__*/_jsx(BaseSaveViewActionButton, {
    disabled: disabled,
    "data-test-id": "save-view-buttons__save",
    onClick: wrappedOnClick,
    tooltipProps: tooltipProps,
    use: "tertiary",
    children: /*#__PURE__*/_jsx(SaveEditableViewIcon, {
      size: "200"
    })
  });
};